import {
    Box,
    Button,
    FormControl,
    FormLabel,
    Input,
    Text,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import PageCard from '../../../components/page-card';
import InlineErrorState from '../../../components/states/InlineErrorState';
import { translate } from '../../../i18n';
import ScopePrompt from '../../_shared/ScopePrompt';
import { classifyRead } from '../../_shared/readState';
import { useSelectedOrganization } from '../../organization/hooks/useSelectedOrganization';
import { useGetUserMembershipTypes } from '../../user-management/hooks/useGetUserMembershipTypes';
import { useUpdateUserMembershipType } from '../../user-management/hooks/useUpdateUserMembershipType';

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const msToDays = (ms: number) => Math.round(ms / MS_PER_DAY);
const daysToMs = (days: number) => days * MS_PER_DAY;

export default function DefaultMembershipTypeCard() {
    const { selectedOrganization } = useSelectedOrganization();
    const toast = useToast();

    /**
     * This card's symptom is not an empty list — it is DISAPPEARANCE, which is
     * why a census keyed on "renders an empty state" never listed it.
     * `if (!defaultType) return null` at the bottom is correct for an org that
     * genuinely has no default membership type, and on a refused or failed
     * `GET organizations/:id/membershipTypes` it is reached for a different
     * reason: `data` is `undefined`, so `find` is `undefined`, so the whole
     * "default membership type" card is absent from the pricing page. The
     * operator reads the absence as "this venue has no default booking limits
     * configured" — a claim nothing on screen is even making, so there is
     * nothing to question.
     */
    const {
        data: membershipTypesData,
        isInitialLoading,
        isError: isMembershipTypesError,
    } = useGetUserMembershipTypes(selectedOrganization?.id);
    const readState = classifyRead(isMembershipTypesError, isInitialLoading, membershipTypesData);
    const membershipTypes = membershipTypesData ?? [];
    const defaultType = membershipTypes.find((t) => t.isOrganizationDefault);

    const { mutateAsync: updateMembershipType, isLoading: isUpdating } = useUpdateUserMembershipType(
        defaultType?.id ?? ''
    );

    const [maxBookings, setMaxBookings] = useState('3');
    const [maxBookingsDurationDays, setMaxBookingsDurationDays] = useState('7');
    const [maxAdvanceDays, setMaxAdvanceDays] = useState('7');

    useEffect(() => {
        if (defaultType) {
            setMaxBookings(String(defaultType.maxPrivateCourtBookings));
            setMaxBookingsDurationDays(String(msToDays(defaultType.maxPrivateCourtBookingsDuration)));
            setMaxAdvanceDays(String(msToDays(defaultType.maxCourtBookingTimeInAdvance)));
        }
    }, [defaultType?.id, defaultType?.maxPrivateCourtBookings, defaultType?.maxPrivateCourtBookingsDuration, defaultType?.maxCourtBookingTimeInAdvance]);

    const parsedBookings = parseInt(maxBookings, 10);
    const parsedDuration = parseInt(maxBookingsDurationDays, 10);
    const parsedAdvance = parseInt(maxAdvanceDays, 10);

    const isDirty = defaultType && (
        maxBookings !== String(defaultType.maxPrivateCourtBookings) ||
        maxBookingsDurationDays !== String(msToDays(defaultType.maxPrivateCourtBookingsDuration)) ||
        maxAdvanceDays !== String(msToDays(defaultType.maxCourtBookingTimeInAdvance))
    );

    const isValid = !isNaN(parsedBookings) && parsedBookings >= 1
        && !isNaN(parsedDuration) && parsedDuration >= 1
        && !isNaN(parsedAdvance) && parsedAdvance >= 1;

    const handleSave = async () => {
        if (!defaultType || !isValid) return;
        try {
            await updateMembershipType({
                name: defaultType.name,
                description: defaultType.description,
                maxPrivateCourtBookings: parsedBookings,
                maxPrivateCourtBookingsDuration: daysToMs(parsedDuration),
                maxCourtBookingTimeInAdvance: daysToMs(parsedAdvance),
                courtBookingDiscountType: defaultType.courtBookingDiscountType,
                courtBookingDiscountValue: defaultType.courtBookingDiscountValue,
                coachingSessionDiscountType: defaultType.coachingSessionDiscountType,
                coachingSessionDiscountValue: defaultType.coachingSessionDiscountValue,
                timeWindowDiscounts: defaultType.timeWindowDiscounts,
            });
            toast({ title: translate('common.save'), status: 'success', duration: 2000 });
        } catch {
            toast({ title: translate('common.error'), status: 'error', duration: 3000, isClosable: true });
        }
    };

    // Order matters: the failed read is checked BEFORE the no-default case, or
    // the two stay indistinguishable and the error branch is unreachable.
    if (readState === 'unreadable') {
        return (
            <PageCard title={translate('userMembership.default_membership_type')}>
                <Box p={4}>
                    <InlineErrorState message={translate('userMembership.error_loading')} />
                </Box>
            </PageCard>
        );
    }

    /**
     * Nobody asked, and the disappearance above is reached a SECOND way.
     * `PricingManagementPage` renders this card unconditionally and has no
     * scope guard, so with no organization selected the read is disabled,
     * `data` is `undefined`, `find` is `undefined` — and the card vanished from
     * the pricing page exactly as it did on the refused read this file was
     * already fixed for. Same symptom, different cause, and only one of the two
     * had been asked about.
     *
     * The card is kept on screen with the scope prompt instead: an absent card
     * is a claim ("this venue has no default booking limits") that nothing on
     * screen is making, so there is nothing for the operator to disbelieve.
     */
    if (readState === 'never-issued') {
        return (
            <PageCard title={translate('userMembership.default_membership_type')}>
                <ScopePrompt gate='organization' />
            </PageCard>
        );
    }

    if (!defaultType) return null;

    return (
        <PageCard title={translate('userMembership.default_membership_type')}>
            <Box p={4}>
                <FormControl>
                    <FormLabel>{translate('userMembership.maxPrivateCourtBookings')}</FormLabel>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Input
                            width="100px"
                            type="number"
                            min={1}
                            value={maxBookings}
                            onChange={(e) => setMaxBookings(e.target.value)}
                        />
                        <Input
                            width="100px"
                            type="number"
                            min={1}
                            value={maxBookingsDurationDays}
                            onChange={(e) => setMaxBookingsDurationDays(e.target.value)}
                        />
                        <Text>{translate('userMembership.days')}</Text>
                    </Box>
                </FormControl>

                <FormControl mt={4}>
                    <FormLabel>{translate('userMembership.maxCourtBookingTimeInAdvance')}</FormLabel>
                    <Box display="flex" alignItems="center" gap={2}>
                        <Input
                            type="number"
                            min={1}
                            value={maxAdvanceDays}
                            onChange={(e) => setMaxAdvanceDays(e.target.value)}
                        />
                        <Text>{translate('userMembership.days')}</Text>
                    </Box>
                </FormControl>

                {isDirty && (
                    <Button mt={4} colorScheme="brand" onClick={handleSave} isLoading={isUpdating} isDisabled={!isValid}>
                        {translate('common.save')}
                    </Button>
                )}
            </Box>
        </PageCard>
    );
}
